import { FilterList } from "@mui/icons-material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import Papa from "papaparse";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  deleteIconStyles,
  iconButtonStyles,
  primaryButtonStyles,
} from "../../../Constants/buttonStyles";
import { tableStyles } from "../../../Constants/tableStyles";
import theme, { scrollbarStyles } from "../../../Constants/theme";
import { resetUserPassword } from "../../../features/auth/authSlice";
import {
  addMultipleUsers,
  addNewUser,
  deleteUser,
  getAllUsers,
  updateUser,
} from "../../../features/organization/orgSlice";
import CreateNewUserDialog from "./ManagementComponents/Users/CreateNewUserDialog";
import DeleteUsersFromOrgDialog from "./ManagementComponents/Users/DeleteUsersFromOrgDialog";
import EditSelectedUserDialog from "./ManagementComponents/Users/EditSelectedUserDialog";
import InactiveUsersNotice from "./ManagementComponents/Users/InactiveUsersNotice";
import ResetUserPasswordDialog from "./ManagementComponents/Users/ResetUserPasswordDialog";
import { GenericSnackbar } from "../../../utils/SnackBars";

const UserManagement = () => {
  const organization = useSelector((state) => state.organization);
  const { user } = useSelector((state) => state.auth);
  const [openDeleteConfirm, setOpenDeleteConfrim] = useState(false);
  const [userNameInput, setUserNameInput] = useState("");
  const [_user, set_User] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openEditPasswordDialog, setOpenEditPasswordDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  //
  const [loading, setLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Snackbar logic
  const [snackbar, setSnackbar] = useState({
    message: "",
    severity: "success",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const [inactiveUsersDialogOpen, setInactiveUsersDialogOpen] = useState(false);

  const [newUser, setNewUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    privilege: "",
    permissions: "",
  });
  const [editingUser, setEditingUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    privilege: "",
    permissions: "",
  });
  const [origEditingUser, setOrigEditingUser] = useState([]);

  // reset passwords dialog functions
  const [editingUserPasswordState, setEditingUserPasswordState] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });
  const [selectedUser, setSelectedUser] = useState(null);

  // to toggle password fields
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordReset, setShowNewPasswordReset] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const allGroupNames = Object.keys(organization?.organization?.groups || []);

  const dispatch = useDispatch();

  const toggleShowNewPassword = () => {
    setShowNewPassword((prev) => !prev);
  };

  const toggleShowNewPasswordReset = () => {
    setShowNewPasswordReset((prev) => !prev);
  };

  const handlePwdOpen = (user) => {
    setOpenEditPasswordDialog(true);
    setSelectedUser(user);
    setEditingUserPasswordState({
      newPassword: "",
      confirmNewPassword: "",
    });
  };

  const handlePwdClose = () => {
    setOpenEditPasswordDialog(false);
    setEditingUserPasswordState({
      newPassword: "",
      confirmNewPassword: "",
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setEditingUserPasswordState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handlePasswordReset = async () => {
    if (
      editingUserPasswordState.newPassword !==
      editingUserPasswordState.confirmNewPassword
    ) {
      // Alert or show error message: Passwords do not match
      alert("New passwords do not match");
      return;
    }

    const passwordRegex = /^(?=.*\d)[A-Za-z\d/\*@!#_-]{8,}$/;

    if (!passwordRegex.test(editingUserPasswordState.newPassword)) {
      setSnackbar({
        message: `Password must be at least 8 characters long and include at least one number.`,
        severity: "warning",
      });
      setSnackbarOpen(true);
      return;
    }

    const res = await dispatch(
      resetUserPassword({
        password: editingUserPasswordState.newPassword,
        user_email: selectedUser.email,
      })
    );
    if (!res.type.includes("Rejected")) {
      setSnackbar({
        message: `User with email ${selectedUser.email} password reset successfully. `,
        severity: "success",
      });
      setSnackbarOpen(true);
    } else {
      setSnackbar({
        message: `Unable to reset ${selectedUser.email} password. `,
        severity: "error",
      });
      setSnackbarOpen(true);
    }

    // Alert for user that password updated
    handlePwdClose();
  };

  // get all users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!usersLoaded) {
        setSnackbar({
          message: "Loading users, please wait...",
          severity: "info",
        });
        setSnackbarOpen(true);
      }
      if (organization?.organization?.users && !usersLoaded) {
        setLoading(true);

        const data = organization.organization.users.map((usr) => usr["$oid"]);
        if (data.length > 0 && users.length === 0 && !organization.isLoading) {
          const response = await dispatch(getAllUsers(data));
          const sortedUsers = [
            ...response.payload.filter(
              (usr) => usr.email === user?.user?.email
            ),
            ...response.payload.filter(
              (usr) => usr.email !== user?.user?.email
            ),
          ];
          setUsers(sortedUsers);
        }

        if (
          !(
            user?.user?.privilege === "owner" ||
            user?.user?.privilege === "admin"
          ) ||
          !(organization?.organization?.subscription !== "starter")
        ) {
          navigate("deployedBots/");
        }

        setLoading(false);
        setSnackbarOpen(false);
        setUsersLoaded(true); // Mark users as loaded
      }
    };

    fetchUsers();
  }, [organization?.organization?.users, usersLoaded]);

  useEffect(() => {
    if (
      usersLoaded && // Ensure users have finished loading
      organization?.organization?.subscription !== "starter" &&
      users.some((user) => user.billing_status === 0)
    ) {
      setInactiveUsersDialogOpen(true);
    }
  }, []);

  const isAlphabetic = (str) => /^[A-Za-z]+$/.test(str);
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPassword = (password) =>
    /^(?=.*\d)[A-Za-z\d\/\-\*\@\!\#\_]{8,}$/.test(password);

  const isFormValid = () =>
    isAlphabetic(newUser.first_name) &&
    isAlphabetic(newUser.last_name) &&
    isValidEmail(newUser.email) &&
    isValidPassword(newUser.password);

  const isEditFormValid = () =>
    isAlphabetic(editingUser.first_name) &&
    isAlphabetic(editingUser.last_name) &&
    isValidEmail(editingUser.email);

  const handleDialogClose = () => {
    setInactiveUsersDialogOpen(false);
  };

  // add new user dialog functions
  const handleOpen = () => {
    setOpenDialog(true);
  };
  const handleClose = () => {
    setOpenDialog(false);
    setNewUser({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      privilege: "",
      permissions: "",
    });
  };

  const handleCheckout = async () => {
    const user_count = organization.users
      .filter((usr) => usr.billing_status === 0 || usr.billing_status === false)
      .length.toString();
    setLoading(true);
    const orgId = user.user.organization["$oid"];

    try {
      if (user_count === "0") {
        throw new TypeError("Users should be greater than 0.");
      }
      // {
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${token}`, // Include JWT for authentication
      //   },
      // }
      const response = await axios.post(
        `/api/payments/subscribe/user/${orgId}/${user_count}`
      );

      if (response.data) {
        window.open(response.data);
      } else {
        setSnackbar({
          message:
            "Error: Failed to initiate payment. Please contact support for assistance.",
          severity: "error",
        });
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbar({
        message:
          "Error: Failed to initiate payment. Please contact support for assistance.",
        severity: "error",
      });
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setSnackbarOpen(false);
    }
  };
  const handleAddUser = async () => {
    // Check if the email already exists
    const emailExists = users.some((user) => user.email === newUser.email);

    if (emailExists) {
      setSnackbar({
        message: "This email already exists. Please use a different email.",
        severity: "warning",
      });
      setSnackbarOpen(true);
      return; // Exit the function if the email already exists
    }
    const passwordRegex = /^(?=.*\d)[A-Za-z\d\/\-\*\@\!\#\_]{8,}$/;
    if (!passwordRegex.test(newUser.password)) {
      setSnackbar({
        message: `Password must be at least 8 characters long and include at least one number.`,
        severity: "warning",
      });
      setSnackbarOpen(true);
      return;
    }
    setSnackbar({
      message: "Adding new user...",
      severity: "info",
    });
    setSnackbarOpen(true);
    const data = {
      ...newUser,
      organization: organization.organization._id["$oid"],
      // billing_status:
      //   organization?.organization?.users.length > 2 ? "INACTIVE" : "ACTIVE",
    };
    const response = await dispatch(addNewUser(data));
    if (response.payload.status === "User Successfuly Created") {
      let new_user_data = JSON.parse(JSON.stringify(response.payload?.user));
      if (typeof new_user_data.groups === "string") {
        try {
          // Extract ObjectId values and convert them into the desired format
          const objectIdMatches = new_user_data.groups.match(
            /ObjectId\('([^']+)'\)/g
          );

          if (objectIdMatches) {
            new_user_data.groups = objectIdMatches.map((match) => {
              const oid = match.match(/ObjectId\('([^']+)'\)/)[1]; // Extract just the ID
              return { $oid: oid }; // Convert to required format
            });
          } else {
            new_user_data.groups = []; // Default to empty array if no ObjectIds found
          }
        } catch (error) {
          console.error(
            "Error parsing groups field:",
            new_user_data.groups,
            error
          );
          new_user_data.groups = []; // Ensure it's always an array
        }
      }
      if (!Array.isArray(new_user_data.groups)) {
        console.error(
          "Expected groups to be an array, received:",
          new_user_data.groups
        );
        new_user_data.groups = [];
      }
      setSnackbar({
        message: "User added successfully",
        severity: "success",
      });
      setSnackbarOpen(true);
      setUsers([...users, new_user_data]);
      setNewUser({ first_name: "", last_name: "", email: "", password: "" }); // Reset the form

      handleClose();
    } else {
      const message =
        response.payload.response.data.status === "Email already taken"
          ? "This email already exists. Please use a different email."
          : "Error occurred during adding of user, please try again.";
      setSnackbar({
        message: message,
        severity: "error",
      });
      setSnackbarOpen(true);
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
  };
  const areFieldsFilled = () => {
    return (
      newUser.first_name.trim() !== "" &&
      newUser.last_name.trim() !== "" &&
      newUser.email.trim() !== "" &&
      newUser.password.trim() !== "" &&
      newUser.privilege !== "" &&
      newUser.permissions !== ""
    );
  };

  // edit user dialog functions
  const handleEditClose = () => {
    setOpenEditDialog(false);
    setEditingUser({
      first_name: "",
      last_name: "",
      email: "",
      privilege: "",
      permissions: "",
    });
  };
  const handleEditOpen = (user) => {
    setEditingUser(user);
    setOrigEditingUser(user); // Store the original user data
    setOpenEditDialog(true);
  };
  const saveUpdatedUser = async () => {
    const user_id = user?.user?._id.$oid;
    const member_email = editingUser.email;
    await dispatch(
      updateUser({ user_id, member_email, userData: editingUser })
    );
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.email === member_email ? { ...user, ...editingUser } : user
      )
    );
    handleEditClose();
  };
  const isUserDataChanged = () => {
    if (!origEditingUser) return false;
    return (
      editingUser.first_name !== origEditingUser.first_name ||
      editingUser.last_name !== origEditingUser.last_name ||
      editingUser.email !== origEditingUser.email ||
      editingUser.privilege !== origEditingUser.privilege ||
      (editingUser.permissions !== origEditingUser.permissions &&
        isEditFormValid())
    );
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingUser((prevUser) => ({
      ...prevUser,
      [name]: value,
    }));
  };

  // delete user
  const handleDeleteUser = async () => {
    const selectedUser = _user;

    if (_user && userNameInput === `${_user.first_name} ${_user.last_name}`) {
      setSnackbar({
        message: "Deleting user...",
        severity: "info",
      });
      setSnackbarOpen(true);
      await dispatch(deleteUser(selectedUser));
      setSnackbar({
        message: "User deleted successfully.",
        severity: "success",
      });
      setOpenDeleteConfrim(false);
      setSnackbarOpen(true);
      const updatedUsers = users.filter((u) => u.email !== selectedUser.email);
      setUsers(updatedUsers);
      setSnackbarOpen(false);
      set_User(null);
      setUserNameInput("");
    }
  };

  const handleOpenDeleteConfirm = (_user) => {
    setOpenDeleteConfrim(true);
    set_User(_user);
  };

  const parseGroups = (groupString) => {
    const regex = /ObjectId\('([^']+)'\)/g;
    const matches = [];
    let match;
    while ((match = regex.exec(groupString)) !== null) {
      matches.push({ $oid: match[1] });
    }
    return matches;
  };

  // bulk add users
  const handleUploadCSV = (e) => {
    const file = e.target.files[0];
    Papa.parse(file, {
      complete: async (result) => {
        const parsedUsers = result.data
          .map((row) => ({
            first_name: row["First Name"],
            last_name: row["Last Name"],
            email: row["Email"],
            password: row["Password"],
            privilege: row["Privilege"],
            permissions: row["Permissions"],
          }))
          .filter((user) => {
            // 1) must have email & password
            if (!user.email || !user.password) return false;

            // 2) privilege must be exactly "member" or "admin"
            const validPrivileges = ["member", "admin"];
            if (!validPrivileges.includes(user.privilege)) return false;

            // 3) permissions must parse to an integer 1, 2, or 3
            const permInt = parseInt(user.permissions, 10);
            if (![1, 2, 3].includes(permInt)) return false;

            // rewrite permissions to an actual number for downstream code
            user.permissions = permInt;
            return true;
          });

        // Show loader notification
        setSnackbar({
          message: "Uploading users, please wait...",
          severity: "info",
        });
        setSnackbarOpen(true);

        try {
          const res = await dispatch(
            addMultipleUsers({
              users: parsedUsers,
              organization: organization.organization._id["$oid"],
            })
          );

          // Format errors for better readability using bullet points
          if (res.payload.errors && res.payload.errors.length > 0) {
            const formattedErrors = res.payload.errors
              .map((err) => `• ${err.email || "Unknown Email"}: ${err.error}`)
              .join("\n");
            setSnackbar({
              message: `Some users could not be added:\n${formattedErrors}`,
              severity: "error",
            });
          } else {
            setSnackbar({
              message: "Users added successfully",
              severity: "success",
            });

            const processedUsers = res.payload.users.map((user) => ({
              ...user,
              groups:
                typeof user.groups === "string"
                  ? parseGroups(user.groups)
                  : user.groups,
            }));

            if (res.payload.users && res.payload.users.length > 0) {
              setUsers((prevUsers) => [...prevUsers, ...processedUsers]);
              setSnackbar({
                message: "Users added successfully",
                severity: "success",
              });
            }
          }
        } catch (error) {
          setSnackbar({
            message: "An unexpected error occurred while uploading users.",
            severity: "error",
          });
        } finally {
          setSnackbarOpen(true);
          setLoading && setLoading(false);
          e.target.value = "";
        }
      },
      header: true,
    });
  };

  const getPermissionName = (permissionNum) => {
    switch (permissionNum) {
      case 1:
        return "Public";
      case 2:
        return "Confidential";
      case 3:
        return "Secret";
      default:
        return permissionNum; // Fallback to the original value if not 1, 2, or 3
    }
  };

  const get_billing_status_name = (billing_status) => {
    switch (billing_status) {
      case 1:
        return "Active";
      case 0:
        return "Inactive";
      default:
        return billing_status; // Fallback to the original value if not 1, 2, or 3
    }
  };

  const get_group_names = (group_ids) => {
    if (!organization?.organization?.groups) return "";

    const groupMap = organization.organization.groups;

    // Create a lookup table: { id: "Group Name" }
    const idToNameMap = Object.entries(groupMap).reduce(
      (acc, [name, idObj]) => {
        acc[idObj.$oid] = name;
        return acc;
      },
      {}
    );

    // Extract group names based on given group_ids and join them into a string
    return group_ids
      .map((group) => idToNameMap[group.$oid]) // Map ID to name
      .filter(Boolean) // Remove undefined values
      .join(", "); // Convert to a comma-separated string
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Open popover when clicking the filter icon
  const handleOpenFilter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Close popover
  const handleCloseFilter = () => {
    setAnchorEl(null);
  };

  // Toggle selection of groups
  const handleGroupSelection = (groupName) => {
    setSelectedGroups(
      (prevSelected) =>
        prevSelected.includes(groupName)
          ? prevSelected.filter((g) => g !== groupName) // Remove if already selected
          : [...prevSelected, groupName] // Add if not selected
    );
  };

  // Function to filter users by selected groups
  const filteredUsers =
    selectedGroups.length === 0
      ? users
      : users.filter((user) =>
          user.groups.some((groupId) =>
            selectedGroups.includes(get_group_names([groupId]))
          )
        );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
        fontFamily: theme.typography.fontFamily,
        padding: "20px",
        height: "100%",
      }}
    >
      {/* Confirm User Delete Dialog */}
      <DeleteUsersFromOrgDialog
        openDeleteConfirm={openDeleteConfirm}
        setOpenDeleteConfrim={setOpenDeleteConfrim}
        setUserNameInput={setUserNameInput}
        set_User={set_User}
        _user={_user}
        userNameInput={userNameInput}
        handleDeleteUser={handleDeleteUser}
      />

      <InactiveUsersNotice
        inactiveUsersDialogOpen={inactiveUsersDialogOpen}
        handleDialogClose={handleDialogClose}
      />
      {(user?.user?.privilege === "owner" ||
        user?.user?.privilege === "admin") && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            color: theme.textColors.dark.primary,
            padding: "16px",
            overflowY: "hidden",
            borderRadius: theme.borderRadius.md,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              py: 2,
              width: "100%",
              mx: 0,
              gap: 2,
              px: 0,
            }}
          >
            <Tooltip title="Add New User">
              <Button
                variant="contained"
                onClick={handleOpen}
                sx={primaryButtonStyles}
              >
                Add User
              </Button>
            </Tooltip>
            {organization?.organization?.subscription !== "starter" &&
              organization?.users.length > 0 &&
              organization?.users?.some(
                (user) => user.billing_status === 0 || !user.billing_status
              ) && (
                <Tooltip title="Activate users">
                  <Button
                    variant="contained"
                    sx={primaryButtonStyles}
                    onClick={handleCheckout}
                  >
                    Checkout
                  </Button>
                </Tooltip>
              )}

            <Tooltip title="The csv should include these as headers: First Name, Last Name, Email, Password">
              <Button
                variant="contained"
                component="label"
                sx={primaryButtonStyles}
              >
                Upload CSV
                <input
                  type="file"
                  hidden
                  onChange={handleUploadCSV}
                  accept=".csv"
                />
              </Button>
            </Tooltip>
          </Box>

          {/* dialog box create new user */}
          <CreateNewUserDialog
            openDialog={openDialog}
            handleChange={handleChange}
            newUser={newUser}
            isAlphabetic={isAlphabetic}
            areFieldsFilled={areFieldsFilled}
            isFormValid={isFormValid}
            handleClose={handleClose}
            isValidPassword={isValidPassword}
            isValidEmail={isValidEmail}
            handleAddUser={handleAddUser}
          />

          {/* dialog box for edit selected user */}
          <EditSelectedUserDialog
            openEditDialog={openEditDialog}
            handleEditClose={handleEditClose}
            editingUser={editingUser}
            handleEditChange={handleEditChange}
            isAlphabetic={isAlphabetic}
            user={user}
            isUserDataChanged={isUserDataChanged}
            saveUpdatedUser={saveUpdatedUser}
          />

          {/* dialog for resetting user's password */}
          <ResetUserPasswordDialog
            openEditPasswordDialog={openEditPasswordDialog}
            showNewPassword={showNewPassword}
            editingUserPasswordState={editingUserPasswordState}
            toggleShowNewPassword={toggleShowNewPassword}
            showNewPasswordReset={showNewPasswordReset}
            toggleShowNewPasswordReset={toggleShowNewPasswordReset}
            handlePasswordChange={handlePasswordChange}
            handlePasswordReset={handlePasswordReset}
            handlePwdClose={handlePwdClose}
          />

          {/* generic snackbar */}

          <GenericSnackbar
            open={snackbarOpen}
            onClose={handleSnackbarClose}
            message={snackbar.message}
            severity={snackbar.severity}
          />

          <TableContainer
            component={Paper}
            sx={{
              ...tableStyles.tableContainer,
              ...scrollbarStyles,
              maxHeight: "70vh",
            }}
          >
            <Table stickyHeader>
              <TableHead sx={tableStyles.tableHead}>
                <TableRow>
                  <TableCell sx={tableStyles.tableHeaderCell} align="left">
                    #
                  </TableCell>
                  <TableCell sx={tableStyles.tableHeaderCell} align="left">
                    Name
                  </TableCell>
                  <TableCell sx={tableStyles.tableHeaderCell} align="left">
                    Email
                  </TableCell>
                  <TableCell sx={tableStyles.tableHeaderCell} align="left">
                    Privilege
                  </TableCell>
                  <TableCell sx={tableStyles.tableHeaderCell} align="left">
                    Permissions
                  </TableCell>
                  <TableCell sx={tableStyles.tableHeaderCell} align="left">
                    Billing Status
                  </TableCell>
                  <TableCell sx={tableStyles.tableHeaderCell} align="left">
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" sx={{ marginRight: "4px" }}>
                        Groups
                      </Typography>

                      {/* Small IconButton to Open Popover */}
                      <IconButton
                        size="small"
                        onClick={handleOpenFilter}
                        sx={iconButtonStyles}
                      >
                        <FilterList
                          fontSize="small"
                          sx={{ fontSize: "14px" }}
                        />
                      </IconButton>

                      {/* Popover for Filter Options */}
                      <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleCloseFilter}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                      >
                        {allGroupNames.map((groupName) => (
                          <MenuItem
                            key={groupName}
                            onClick={() => handleGroupSelection(groupName)}
                          >
                            <Checkbox
                              checked={selectedGroups.includes(groupName)}
                            />
                            {groupName}
                          </MenuItem>
                        ))}
                      </Popover>
                    </Box>
                  </TableCell>
                  <TableCell sx={tableStyles.tableHeaderCell} align="left">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!usersLoaded ? (
                  <TableRow
                    sx={{
                      ...tableStyles.tableRow,
                      "&:last-child td": { borderBottom: "none" },
                    }}
                  >
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={tableStyles.tableCell}
                    >
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.length > 0 &&
                  filteredUsers.map((_user, index) => (
                    <TableRow
                      key={`user-${
                        _user._id?.["$oid"] || _user.email || index
                      }`}
                      sx={{
                        ...tableStyles.tableRow,
                        "&:last-child td": { borderBottom: "none" },
                      }}
                    >
                      <TableCell sx={tableStyles.tableCell} align="left">
                        {index + 1} {/* Dynamically display the row number */}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell} align="left">
                        {`${_user["first_name"]} ${_user["last_name"]}`}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell} align="left">
                        {_user["email"]}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell} align="left">
                        {capitalizeFirstLetter(_user["privilege"])}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell} align="left">
                        {getPermissionName(_user["permissions"])}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell} align="left">
                        {get_billing_status_name(_user["billing_status"])}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell} align="left">
                        {get_group_names(_user["groups"])}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {_user.email !== user?.user?.email && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: "8px",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {!(
                              user?.user?.privilege === "admin" &&
                              _user?.privilege === "owner"
                            ) && (
                              <div style={{ height: "40px" }}>
                                <Tooltip title="Edit User">
                                  <IconButton
                                    onClick={() => handleEditOpen(_user)}
                                    sx={{
                                      "&:hover .MuiSvgIcon-root": {
                                        color: "#007bff",
                                      },
                                    }}
                                  >
                                    <EditIcon
                                      sx={{ color: "#E5EFFF" }}
                                      size="small"
                                    />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            )}
                            {!(
                              user?.user?.privilege === "admin" &&
                              _user?.privilege === "owner"
                            ) && (
                              <div style={{ height: "40px" }}>
                                <Tooltip title="Reset User Password">
                                  <IconButton
                                    onClick={() => handlePwdOpen(_user)}
                                    sx={{
                                      "&:hover .MuiSvgIcon-root": {
                                        color: "#e9a70c",
                                      },
                                    }}
                                  >
                                    <LockResetIcon
                                      sx={{ color: "#E5EFFF" }}
                                      size="small"
                                    />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            )}
                            <div style={{ height: "40px" }}>
                              {!(
                                user?.user?.privilege === "admin" &&
                                _user?.privilege === "owner"
                              ) && (
                                <Tooltip title="Delete User">
                                  <IconButton
                                    onClick={() =>
                                      handleOpenDeleteConfirm(_user)
                                    }
                                    sx={deleteIconStyles}
                                  >
                                    <DeleteOutline
                                      // sx={{ color: "#E5EFFF" }}
                                      size="small"
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default UserManagement;
